import { useEffect, useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import {
  Box,
  Flex,
  Link,
  Stack,
  useToast,
  Text,
  Button,
  HStack,
  IconButton,
  InputGroup,
  InputLeftElement,
  Input,
  Select,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { useEnsureAuthenticated } from "@/contexts/ensureAuthenticated";
import { userPermissions } from "@/contexts/userPermissions";
import { PermissionEnum } from "@/utils/permissionEnum";
import { CiReceipt, CiSearch } from "react-icons/ci";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from "@chakra-ui/icons";
import { getCriteriaEngines, updateCriteriaEngine } from "@/services/consignado/criteriaEngine.service";
import { CriteriaEngineResponse } from "@/models/consignado/criteriaEngine.model";

export default function ConsignadoCriterionEngineListPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPage, setTotalPage] = useState(1);
  const { user } = useEnsureAuthenticated();
  const permissions = userPermissions(user);
  const toast = useToast();
  const queryCache = useQueryClient()
  const backgroundStyle = useConditionalBackground();

  const [sortBy, setSortBy] = useState<"id" | "nome" | "descricao" | "status">("id");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("ASC");

  const [search, setSearch] = useState("");

  const handleSort = async (field: "id" | "nome" | "descricao" | "status") => {
    setSortBy(field);
    setSortDirection(
      sortBy === field && sortDirection === "ASC" ? "DESC" : "ASC"
    );
  };

  const { data } = useQuery<CriteriaEngineResponse>({
    queryKey: ["criteria-engines", page, pageSize, sortBy, sortDirection],
    queryFn: async () => await getCriteriaEngines(page, pageSize, sortBy, sortDirection),
    staleTime: 100 * 1000,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (data?.total_paginas) {
      setTotalPage(data.total_paginas)
    }
  }, [data])

  const handleDeactivateCriteriaEngine = useMutation({
    mutationFn: async (data: {id: number, status: boolean}) => {
      await updateCriteriaEngine(data)
    },
    onSuccess(_, variables){
      toast({
        title: "Sucesso",
        description: `Motor de crédito ${variables.status? "ativado" : "desativado"} com successo`,
        status: "success",
        duration: 3000,
        isClosable: true,
      })

      const criteriaEngines = queryCache.getQueryData<CriteriaEngineResponse>(["criteria-engines", page, pageSize, sortBy, sortDirection])
     
      const updatedCriteriaEngine = criteriaEngines?.itens.map((item) => {
        return item.id === variables.id ? { ...item, status: variables.status } : item
        }
      );

      const criteriaEngineUpdated = {
        ...criteriaEngines,
        itens: updatedCriteriaEngine
      }; 

      queryCache.setQueryData(
        ["criteria-engines", page, pageSize, sortBy, sortDirection],
        criteriaEngineUpdated
      );
    },
    onError(){
      toast({
        title: "Erro",
        description: "Erro ao desativar motor de crédito",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  })
  
  return (
    <Box
      padding={{ base: "2", md: "4" }}
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
    >
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        mb={4}
        color="white"
        boxShadow="md"
        width="100%"
      >
        <Flex
          alignItems={{ base: "center", md: "center" }}
          justifyContent="space-between"
          flexDirection={{ base: "column", md: "row" }}
          textAlign={{ base: "center", md: "left" }}
        >
          <Flex alignItems="center" mb={{ base: 4, md: 0 }}>
            <CiReceipt size="2rem" />
            <Text
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              ml={4}
              fontFamily="Lexend Deca"
            >
              Critérios de Crédito Consignado
            </Text>
          </Flex>

          <Flex
            flexDirection={{ base: "column", md: "row" }}
            alignItems="flex-end"
            mt={{ base: 4, md: 0 }}
            width="100%"
            justifyContent={{ base: "center", md: "flex-end" }}
            gap={4}
          >
            {permissions.some(
              (permission) => permission === PermissionEnum.CREATE_USER
            ) && (
                <Stack
                  direction={{ base: "column", md: "row" }}
                  spacing={4}
                  width="100%"
                  justifyContent={{ base: "center", md: "end" }}
                >
                  <Link
                    as={RouterLink}
                    to="novo"
                    bg="white"
                    color="black"
                    px={4}
                    py={{ base: 4, md: 2 }}
                    borderRadius="md"
                    fontWeight="bold"
                    _hover={{ bg: "gray.200" }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize={{ base: "lg", md: "md" }}
                    height={{ base: "56px", md: "40px" }}
                    width={{ base: "100%", md: "auto" }}
                    fontFamily="Lexend Deca"
                  >
                    <CiReceipt style={{ marginRight: "8px", fontSize: "20px" }} />
                    Cadastrar Critério
                  </Link>
                </Stack>
              )}
          </Flex>
        </Flex>
        <Flex w="30%" mt={8} gap={2}>
          <InputGroup>
            <InputLeftElement mt={1} pointerEvents="none">
              <CiSearch color="black" size="2rem" />
            </InputLeftElement>
            <Input
              height={{ base: "56px", md: "48px" }}
              bg={"white"}
              placeholder="Pesquisar..."
              variant="filled"
              type="text"
              maxLength={30}
              color="black"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              _focus={{ bg: "white" }}
              _hover={{ bg: "white" }}
              fontFamily="Lexend Deca"
            />
          </InputGroup>
        </Flex>
      </Box>
      <Flex
        justify="center"
        alignItems="center"
        mb={4}
        flexDirection={{ base: "column", md: "row" }}
        width="100%"
        maxWidth="1200px"
        px={{ base: 2, md: 0 }}
      >
        <Flex alignItems="center" mb={{ base: 2, md: 0 }}>
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={() => setPage(data?.pagina_anterior)}
            isDisabled={page === 1}
            mr={2}
            aria-label="Previous Page"
            size={{ base: "md", md: "sm" }}
          />
          <Text fontSize={{ base: "lg", md: "md" }} fontFamily="Lexend Deca">
            Página {data?.pagina_atual || page} de {totalPage}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={() => setPage(data?.proxima_pagina)}
            isDisabled={data?.pagina_atual === data?.total_paginas}
            ml={2}
            aria-label="Next Page"
            size={{ base: "md", md: "sm" }}
          />
        </Flex>
        <Box ml={{ base: 0, md: 4 }} mt={{ base: 2, md: 0 }}>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            width={{ base: "100%", md: "auto" }}
            size={{ base: "md", md: "sm" }}
            fontSize={{ base: "lg", md: "md" }}
            fontFamily="Lexend Deca"
          >
            <option value={10}>10 por página</option>
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </Select>
        </Box>
      </Flex>
      
      <Box width="100%" overflow="visible" >
        <TableContainer borderRadius="md" boxShadow="md" >
          <Table size="sm" w='100%'>
            <Thead
              {...backgroundStyle}
              position="sticky"
              top={0}
              zIndex={1}
              boxShadow="md"
            >
              <Tr>
                <Th color={'white'} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>AÇÕES</Text>
                  </HStack>
                </Th>
                <Th color={'white'} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>Id</Text>
                    <IconButton
                      icon={
                        sortBy === "id" && sortDirection === "ASC" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by Id"
                      bg={"gray.200"}
                      onClick={() => handleSort("id")}
                    />
                  </HStack>
                </Th>
                <Th color={'white'} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>NOME</Text>
                    <IconButton
                      icon={
                        sortBy === "nome" && sortDirection === "ASC" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by Name"
                      bg={"gray.200"}
                      onClick={() => handleSort("nome")}
                    />
                  </HStack>
                </Th>

                <Th color={'white'} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>Descrição</Text>
                    <IconButton
                      icon={
                        sortBy === "descricao" && sortDirection === "ASC" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by Percentage"
                      bg={"gray.200"}
                      onClick={() => handleSort("descricao")}
                    />
                  </HStack>
                </Th>            

                <Th color={'white'} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>ATIVO</Text>
                    <IconButton
                      icon={
                        sortBy === "status" && sortDirection === "ASC" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by enabled"
                      bg={"gray.200"}
                      onClick={() => handleSort("status")}
                    />
                  </HStack>
                </Th>
              </Tr>
            </Thead>
            <Tbody>

            {data?.itens?.map((criteriaEngine) => (
                <Tr
                  key={criteriaEngine.id}
                  transition="all 0.3s ease-in-out"
                  borderBottom="1px solid"
                  fontFamily="Lexend Deca"

                >
                  <Td
                    textAlign="center"
                    fontSize={{ base: "lg", md: "sm" }}
                    py={{ base: 2, md: 3 }}
                    fontFamily="Lexend Deca"
                    width={"250px"}
                  >
                    <Flex justify="center" flexDirection="column" gap={2}>
                      {criteriaEngine.status ? (
                        <Button
                          size={{ base: "md", md: "md" }}
                          colorScheme="red"
                          onClick={() => handleDeactivateCriteriaEngine.mutate({id: criteriaEngine.id, status: !criteriaEngine.status})}
                          width="100%"
                          py={{ base: 2.5, md: 2 }}
                          fontSize={{ base: "md", md: "sm" }}
                          fontWeight="bold"
                          fontFamily="Lexend Deca"
                        >
                          Desativar Critério
                        </Button>
                      ) : (
                        <Button
                          size={{ base: "md", md: "md" }}
                          colorScheme="green"
                          onClick={() => handleDeactivateCriteriaEngine.mutate({id: criteriaEngine.id, status: !criteriaEngine.status})}
                          width="100%"
                          py={{ base: 2.5, md: 2 }}
                          fontSize={{ base: "md", md: "sm" }}
                          fontWeight="bold"
                          fontFamily="Lexend Deca"
                        >
                          Ativar Critério
                        </Button>
                      )}
                    </Flex>
                  </Td>

                  <Td textAlign="center" fontSize="sm" py={{ base: 2, md: 3 }}>
                    <Link
                      as={RouterLink}
                      to={`/consignado/criterios-credito/${criteriaEngine.id}`}
                      color="blue.500"
                    >
                    {criteriaEngine.id}
                    </Link>
                  </Td>

                  <Td textAlign="center" fontSize="sm" py={{ base: 2, md: 3 }}>
                     <Text whiteSpace="normal" wordBreak="break-word">
                      {criteriaEngine.nome}
                     </Text>
                  </Td>

                  <Td textAlign="center" fontSize="sm" py={{ base: 2, md: 3 }} >
                    <Text whiteSpace="normal" wordBreak="break-word">
                     {criteriaEngine.descricao}
                    </Text>
                  </Td>

                  <Td textAlign="center" fontSize="sm" py={{ base: 2, md: 3 }}>
                    {criteriaEngine.status ? "Ativo" : "Inativo"}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      <Flex
        justify="center"
        alignItems="center"
        flexDirection={{ base: "column", md: "row" }}
        width="100%"
        maxWidth="1200px"
        px={{ base: 2, md: 0 }}
      >
        <Flex alignItems="center" mt={4} mb={{ base: 2, md: 2 }}>
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={() => setPage(data?.pagina_anterior)}
            isDisabled={page === 1}
            mr={2}
            aria-label="Previous Page"
            size={{ base: "md", md: "sm" }}
          />
          <Text fontSize={{ base: "lg", md: "md" }} fontFamily="Lexend Deca">
            Página {data?.pagina_atual} de {totalPage}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={() => setPage(data?.proxima_pagina)}
            isDisabled={data?.pagina_atual === data?.total_paginas}
            ml={2}
            aria-label="Next Page"
            size={{ base: "md", md: "sm" }}
          />
        </Flex>
        <Box ml={{ base: 0, md: 4 }} mb={2} mt={{ base: 2, md: 4 }}>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            width={{ base: "100%", md: "auto" }}
            size={{ base: "md", md: "sm" }}
            fontSize={{ base: "lg", md: "md" }}
            fontFamily="Lexend Deca"
          >
            <option value={10}>10 por página</option>
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </Select>
        </Box>
      </Flex>
    </Box>
  );
}
